import { useState, useCallback, useRef } from 'react';
import { Shape } from '../types/Shape';
import {
  PDFExportOptions,
  JPGExportOptions,
  ExportProgress,
  ExportScope,
} from '../types/Export';
import {
  executeExport,
  calculateBoundingBox,
  calculateViewportBounds,
} from '../utils/exportUtils';

interface UseExportProps {
  shapes: Shape[];
  selectedShapes: string[];
  stagePos: { x: number; y: number };
  stageScale: number;
  viewportWidth: number;
  viewportHeight: number;
  canvasContainerRef: React.RefObject<HTMLDivElement>;
}

interface UseExportReturn {
  isExportModalOpen: boolean;
  exportProgress: ExportProgress | null;
  openExportModal: () => void;
  closeExportModal: () => void;
  handleExport: (options: PDFExportOptions | JPGExportOptions) => Promise<void>;
  quickExportPDF: () => void;
  quickExportJPG: () => void;
}

export const useExport = ({
  shapes,
  selectedShapes,
  stagePos,
  stageScale,
  viewportWidth,
  viewportHeight,
  canvasContainerRef,
}: UseExportProps): UseExportReturn => {
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportProgress, setExportProgress] = useState<ExportProgress | null>(null);

  // 클린업을 위한 ref
  const cleanupRef = useRef<(() => void) | null>(null);

  const openExportModal = useCallback(() => {
    setExportProgress(null);
    setIsExportModalOpen(true);
  }, []);

  const closeExportModal = useCallback(() => {
    setIsExportModalOpen(false);
    setExportProgress(null);
  }, []);

  /**
   * 내보내기를 위해 캔버스 요소를 준비하고 클론합니다.
   */
  const prepareExportElement = useCallback(
    (scope: ExportScope): HTMLElement | null => {
      const container = canvasContainerRef.current;
      if (!container) return null;

      // 캔버스 컨테이너를 복제
      const clonedContainer = container.cloneNode(true) as HTMLElement;

      // 불필요한 요소 제거 (툴바, 미니맵 등)
      const removeSelectors = [
        '.toolbar-container',
        '.minimap-container',
        '.export-modal-overlay',
        '[class*="resize-handle"]',
        '[class*="connection-point"]',
        '.alignment-guide',
      ];

      removeSelectors.forEach((selector) => {
        const elements = clonedContainer.querySelectorAll(selector);
        elements.forEach((el) => el.remove());
      });

      // 선택 표시 스타일 제거
      const selectedElements = clonedContainer.querySelectorAll('[class*="selected"]');
      selectedElements.forEach((el) => {
        el.classList.remove('selected');
        (el as HTMLElement).style.outline = 'none';
        (el as HTMLElement).style.boxShadow = 'none';
      });

      // 유튜브 iframe을 썸네일로 교체
      const youtubeContainers = clonedContainer.querySelectorAll('[class*="youtube"]');
      youtubeContainers.forEach((ytContainer) => {
        const iframe = ytContainer.querySelector('iframe');
        if (iframe) {
          const src = iframe.getAttribute('src') || '';
          const videoIdMatch = src.match(/embed\/([^?]+)/);
          const videoId = videoIdMatch ? videoIdMatch[1] : '';

          if (videoId) {
            const thumbnail = document.createElement('div');
            thumbnail.style.width = '100%';
            thumbnail.style.height = '100%';
            thumbnail.style.backgroundImage = `url(https://img.youtube.com/vi/${videoId}/hqdefault.jpg)`;
            thumbnail.style.backgroundSize = 'cover';
            thumbnail.style.backgroundPosition = 'center';
            thumbnail.style.display = 'flex';
            thumbnail.style.alignItems = 'center';
            thumbnail.style.justifyContent = 'center';

            // 플레이 버튼 오버레이
            const playIcon = document.createElement('div');
            playIcon.innerHTML = `
              <svg width="68" height="48" viewBox="0 0 68 48" fill="none">
                <path d="M66.52 7.74c-.78-2.93-3.08-5.24-6.01-6.01C55.23.5 34 .5 34 .5S12.77.5 7.49 1.73c-2.93.77-5.23 3.08-6.01 6.01C.25 13.03.25 24 .25 24s0 10.97 1.23 16.26c.78 2.93 3.08 5.24 6.01 6.01C12.77 47.5 34 47.5 34 47.5s21.23 0 26.51-1.23c2.93-.77 5.23-3.08 6.01-6.01C67.75 34.97 67.75 24 67.75 24s0-10.97-1.23-16.26z" fill="#FF0000"/>
                <path d="M27.25 34.25L45.25 24l-18-10.25v20.5z" fill="#FFF"/>
              </svg>
            `;
            playIcon.style.opacity = '0.9';
            thumbnail.appendChild(playIcon);

            iframe.replaceWith(thumbnail);
          }
        }
      });

      // 오디오 플레이어를 아이콘으로 교체
      const audioContainers = clonedContainer.querySelectorAll('[class*="audio"]');
      audioContainers.forEach((audioContainer) => {
        const audio = audioContainer.querySelector('audio');
        if (audio) {
          const audioPlaceholder = document.createElement('div');
          audioPlaceholder.style.width = '100%';
          audioPlaceholder.style.height = '100%';
          audioPlaceholder.style.display = 'flex';
          audioPlaceholder.style.alignItems = 'center';
          audioPlaceholder.style.justifyContent = 'center';
          audioPlaceholder.style.gap = '8px';
          audioPlaceholder.style.backgroundColor = '#f3f4f6';
          audioPlaceholder.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="2">
              <path d="M9 18V5l12-2v13"/>
              <circle cx="6" cy="18" r="3"/>
              <circle cx="18" cy="16" r="3"/>
            </svg>
            <span style="color: #6b7280; font-size: 14px;">Audio</span>
          `;
          audio.replaceWith(audioPlaceholder);
        }
      });

      // 범위에 따른 처리
      if (scope === 'viewport') {
        // 뷰포트 영역만 캡처
        clonedContainer.style.width = `${viewportWidth}px`;
        clonedContainer.style.height = `${viewportHeight}px`;
        clonedContainer.style.overflow = 'hidden';
      } else if (scope === 'selection') {
        // 선택된 요소들의 바운딩 박스 계산
        const selectedShapeObjects = shapes.filter((s) => selectedShapes.includes(s.id));
        const bounds = calculateBoundingBox(selectedShapeObjects, 40);

        // 스타일 조정
        clonedContainer.style.position = 'relative';
        clonedContainer.style.width = `${bounds.width * stageScale}px`;
        clonedContainer.style.height = `${bounds.height * stageScale}px`;

        // 선택되지 않은 요소 숨기기
        const allShapeElements = clonedContainer.querySelectorAll('[data-shape-id]');
        allShapeElements.forEach((el) => {
          const shapeId = el.getAttribute('data-shape-id');
          if (shapeId && !selectedShapes.includes(shapeId)) {
            (el as HTMLElement).style.visibility = 'hidden';
          }
        });
      } else {
        // 전체 캔버스 - 모든 shapes의 바운딩 박스 계산
        const bounds = calculateBoundingBox(shapes, 40);

        clonedContainer.style.width = `${bounds.width * stageScale}px`;
        clonedContainer.style.height = `${bounds.height * stageScale}px`;
      }

      return clonedContainer;
    },
    [shapes, selectedShapes, stagePos, stageScale, viewportWidth, viewportHeight, canvasContainerRef]
  );

  /**
   * 내보내기 실행
   */
  const handleExport = useCallback(
    async (options: PDFExportOptions | JPGExportOptions): Promise<void> => {
      const container = canvasContainerRef.current;
      if (!container) {
        setExportProgress({
          status: 'error',
          progress: 0,
          message: '캔버스를 찾을 수 없습니다.',
        });
        return;
      }

      // 이전 cleanup이 있으면 실행
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }

      let exportElement: HTMLElement | null = null;

      try {
        setExportProgress({
          status: 'preparing',
          progress: 5,
          message: '내보내기 준비 중...',
        });

        // scope에 따라 적절한 요소 준비
        if (options.scope === 'selection' && selectedShapes.length === 0) {
          setExportProgress({
            status: 'error',
            progress: 0,
            message: '선택된 요소가 없습니다. 요소를 선택하거나 다른 범위를 선택하세요.',
          });
          return;
        }

        // prepareExportElement를 호출하여 scope에 맞는 요소 준비
        exportElement = prepareExportElement(options.scope);

        if (!exportElement) {
          setExportProgress({
            status: 'error',
            progress: 0,
            message: '내보내기 요소를 준비할 수 없습니다.',
          });
          return;
        }

        // 임시로 document에 추가 (스타일 계산을 위해)
        exportElement.style.position = 'absolute';
        exportElement.style.left = '-9999px';
        exportElement.style.top = '-9999px';
        exportElement.style.zIndex = '-1';
        document.body.appendChild(exportElement);

        // cleanup 함수 저장
        const elementToCleanup = exportElement;
        cleanupRef.current = () => {
          if (elementToCleanup && elementToCleanup.parentNode) {
            document.body.removeChild(elementToCleanup);
          }
        };

        // 내보내기 실행
        await executeExport(exportElement, options, setExportProgress);
      } catch (error) {
        console.error('Export failed:', error);
        setExportProgress({
          status: 'error',
          progress: 0,
          message: `내보내기 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`,
        });
      } finally {
        // cleanup 실행
        if (cleanupRef.current) {
          cleanupRef.current();
          cleanupRef.current = null;
        }
      }
    },
    [canvasContainerRef, selectedShapes.length, prepareExportElement]
  );

  /**
   * 빠른 PDF 내보내기 (기본 옵션 사용)
   */
  const quickExportPDF = useCallback(() => {
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10);

    handleExport({
      format: 'pdf',
      scope: 'all',
      filename: `notesense-${dateStr}`,
      paperSize: 'A4',
      orientation: 'landscape',
      margin: 10,
    });
  }, [handleExport]);

  /**
   * 빠른 JPG 내보내기 (기본 옵션 사용)
   */
  const quickExportJPG = useCallback(() => {
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10);

    handleExport({
      format: 'jpg',
      scope: 'all',
      filename: `notesense-${dateStr}`,
      quality: 'high',
      scale: 2,
      backgroundColor: '#ffffff',
    });
  }, [handleExport]);

  return {
    isExportModalOpen,
    exportProgress,
    openExportModal,
    closeExportModal,
    handleExport,
    quickExportPDF,
    quickExportJPG,
  };
};
